import { useState } from "react";
import { confirmEmailChange, requestEmailChange } from "../../lib/auth";
import { ApiError, errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";
import { PasswordField, TextField } from "./FormField";

interface ChangeEmailProps {
  label: string;
  email: string;
  /** Grey line under the address. */
  hint?: string;
  /** Called with the new address once it is confirmed. */
  onChanged: (email: string) => void;
  full?: boolean;
}

/**
 * Login e-mail with a "Change" flow: new address + current password, then the
 * 6-digit code sent to the new address (POST /auth/change-email[/confirm]).
 * The address only changes after the code is confirmed.
 */
export default function ChangeEmail({ label, email, hint, onChanged, full }: ChangeEmailProps) {
  const t = useT();
  const c = t.changeEmail;
  const [step, setStep] = useState<"idle" | "request" | "code">("idle");
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("idle");
    setNewEmail("");
    setPassword("");
    setCode("");
    setErrors({});
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErrors({});
    try {
      await fn();
    } catch (err) {
      if (err instanceof ApiError && err.errors) setErrors(err.errors);
      else toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const send = () =>
    run(async () => {
      toast.success(await requestEmailChange(newEmail.trim(), password));
      setPassword("");
      setStep("code");
    });

  const confirm = () =>
    run(async () => {
      const res = await confirmEmailChange(code);
      toast.success(res.message);
      onChanged(res.email);
      reset();
    });

  return (
    <div className={cn("flex flex-col gap-1.5", full && "sm:col-span-2")}>
      <span className="text-[13px] font-medium text-ink-soft">{label}</span>
      <div className="flex gap-2">
        <input value={email} readOnly aria-label={label} className="h-11 min-w-0 flex-1 rounded border border-transparent bg-surface px-3.5 text-sm text-muted outline-none" />
        {step === "idle" && (
          <button type="button" onClick={() => setStep("request")} className="h-11 shrink-0 cursor-pointer rounded border-0 bg-surface px-3.5 text-sm font-medium text-ink hover:bg-surface-hover">
            {c.change}
          </button>
        )}
      </div>
      {step === "idle" && hint && <span className="text-xs text-muted">{hint}</span>}

      {step !== "idle" && (
        <div
          className="mt-1 grid gap-3 rounded border border-line-strong p-3.5"
          // Inside the profile <form>: Enter runs this step instead of saving the profile
          onKeyDown={(e) => {
            if (e.key !== "Enter" || (e.target as HTMLElement).tagName !== "INPUT") return;
            e.preventDefault();
            if (!busy) void (step === "request" ? send() : confirm());
          }}
        >
          <p className="m-0 text-xs text-muted">{c.hint}</p>
          {step === "request" ? (
            <>
              <TextField label={c.newEmail} type="email" autoComplete="email" placeholder={t.common.emailPlaceholder} value={newEmail} error={errors.newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              <PasswordField label={c.password} autoComplete="current-password" placeholder={t.common.ph.password} value={password} error={errors.password} onChange={(e) => setPassword(e.target.value)} />
            </>
          ) : (
            <TextField
              label={c.code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="••••••"
              value={code}
              error={errors.code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || (step === "request" ? !newEmail.trim() || !password : code.length !== 6)}
              onClick={() => void (step === "request" ? send() : confirm())}
              className="h-10 cursor-pointer rounded border-0 bg-brand px-4 text-sm font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {step === "request" ? c.sendCode : c.confirm}
            </button>
            <button type="button" onClick={reset} className="h-10 cursor-pointer rounded border-0 bg-surface px-4 text-sm font-medium text-ink hover:bg-surface-hover">
              {c.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
